import * as React from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import { Check, ChevronDown, ChevronUp } from "lucide-react";

import { cn } from "@/lib/utils";

const Select = SelectPrimitive.Root;

const SelectGroup = SelectPrimitive.Group;

const SelectValue = SelectPrimitive.Value;

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(
      /* `min-w-0` en el campo y en el span del valor: `line-clamp-1` corta el
         alto pero no impide que un flex-item crezca hasta su contenido, así que
         un producto de nombre largo YA SELECCIONADO seguía estirando el campo —
         y con él la ventana. */
      "flex h-10 w-full min-w-0 items-center justify-between gap-2 rounded-xl border border-[#b6dded] bg-white px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:border-primary/60 data-[state=open]:border-primary/60 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:min-w-0 [&>span]:truncate [&>span]:text-left",
      className,
    )}
    {...props}
  >
    {children}
    <SelectPrimitive.Icon asChild>
      <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
));
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn("flex cursor-default items-center justify-center py-1", className)}
    {...props}
  >
    <ChevronUp className="h-4 w-4" />
  </SelectPrimitive.ScrollUpButton>
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn("flex cursor-default items-center justify-center py-1", className)}
    {...props}
  >
    <ChevronDown className="h-4 w-4" />
  </SelectPrimitive.ScrollDownButton>
));
SelectScrollDownButton.displayName = SelectPrimitive.ScrollDownButton.displayName;

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>((
  {
    className,
    children,
    position = "popper",
    /* DEBAJO SI CABE; ENCIMA SI NO CABE. Y EN TODA LA APP (01-09-2026).

       Un desplegable cuelga de su campo: eso es lo primero, y por eso `side` es
       `bottom`. Pero «siempre debajo, pase lo que pase» tampoco valía, y costó
       dos vueltas averiguarlo.

       Clavado abajo, el alto lo decidía el hueco que quedara por debajo del
       campo — y dentro de una hoja casi nunca queda: «Tratamiento» está a dos
       tercios de la pantalla, así que la lista se quedaba en unos 150 px, dos
       filas entre dos flechitas, ilegible.

       Con el volteo puesto, Radix la sube SOLO cuando de verdad no cabe debajo,
       que es lo que hace cualquier desplegable. Lo que hacía insufrible al
       volteo la primera vez no era el volteo: eran 24rem de lista tapando la
       ventana entera, título incluido. Con el techo en 18rem —y limitado además
       por el hueco real— sube lo justo y se lee.

       Y lo que cabe debajo se queda debajo: dos opciones («L» / «L/ha») siguen
       siendo dos opciones colgando del campo. Quien necesite otra cosa sigue
       pudiendo pasar `side` o `avoidCollisions`. */
    side = "bottom",
    sideOffset = 4,
    collisionPadding = 12,
    ...props
  },
  ref,
) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      side={side}
      sideOffset={sideOffset}
      collisionPadding={collisionPadding}
      className={cn(
        /* TOPE DE ANCHO. Sin él la ventana crece hasta que cabe la opción más
           larga: un nombre de producto de los largos («NITRATO DE CALCIO
           STANDARD DELTASOL 25 KG») estiraba el desplegable más allá del marco
           de la hoja que lo contiene. El tope es el menor de: el hueco que Radix
           mide hasta el borde de la pantalla, y 26rem de diseño — en pantalla
           ancha tampoco interesa un desplegable enorme.

           EL SUELO VA AQUÍ, NO EN EL VIEWPORT, y esto costó un fallo reportado
           (31-08-2026: el desplegable de «Tratamiento» salía más estrecho que su
           campo y cortaba las píldoras «Biológico»/«Químico» por la derecha).

           El comentario que había aquí decía que el `min-w-[trigger-width]` del
           viewport «manda sobre esto porque min gana a max en CSS». Min gana a
           max, sí — pero SOLO en el mismo elemento. El suelo estaba en el hijo y
           el techo en el padre, y ahí no compiten: el hijo se pone a lo suyo, el
           padre se queda en su techo, y lo que sobra lo recorta el
           `overflow-hidden` de este mismo padre. Justo lo que se veía.

           Con el suelo TAMBIÉN aquí, min y max son del mismo elemento y ahora sí
           gana el suelo: el desplegable nunca es más estrecho que su campo, y el
           techo sigue frenando el crecimiento por culpa del texto. Con `8rem` de
           respaldo por si `--radix-select-trigger-width` no existe (Radix solo
           la publica en modo `popper`). */
        /* TOPE DE ALTO: el hueco real hasta el borde de la pantalla, y como
           mucho 18rem de diseño. Eran 24rem fijos, y ahí estaba la mitad del
           problema: 384 px de lista encima de un campo a media hoja tapan la
           ventana entera. Con 18rem, voltear deja de doler. */
        "relative z-50 max-h-[min(18rem,var(--radix-select-content-available-height,18rem))] min-w-[var(--radix-select-trigger-width,8rem)] max-w-[min(var(--radix-select-content-available-width,26rem),26rem)] overflow-hidden rounded-xl border bg-white text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        position === "popper" &&
          "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
        className,
      )}
      position={position}
      {...props}
    >
      <SelectScrollUpButton />
      <SelectPrimitive.Viewport
        className={cn(
          "p-1",
          position === "popper" &&
            "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]",
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
      <SelectScrollDownButton />
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
));
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label ref={ref} className={cn("py-1.5 pl-8 pr-2 text-sm font-semibold", className)} {...props} />
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      /* El texto de la opción se corta con puntos suspensivos en vez de empujar
         el ancho. `min-w-0` sobre el hijo flexible es imprescindible: sin él el
         mínimo de contenido de un flex-item es su texto entero y `truncate` no
         llega a activarse nunca. El span del indicador va en absoluto, así que
         `span:last-child` es siempre el del texto. */
      "relative flex w-full min-w-0 cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 focus:bg-accent focus:text-accent-foreground",
      "[&>span:last-child]:min-w-0 [&>span:last-child]:truncate",
      className,
    )}
    {...props}
  >
    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        <Check className="h-4 w-4" />
      </SelectPrimitive.ItemIndicator>
    </span>

    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator ref={ref} className={cn("-mx-1 my-1 h-px bg-muted", className)} {...props} />
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
};
